import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { getPulseStatus, submitPulse, getPulseResults, setPulseQuestion } from "../api";
import { LoadingOverlay } from "../components/LoadingOverlay";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";
const DEFAULT_QUESTION = "How likely are you to recommend working here to a friend?";

function scoreColor(n: number) {
  if (n >= 9) return "#16a34a";
  if (n >= 7) return "#f59e0b";
  return "#dc2626";
}

export default function PulsePage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isHR = ["admin", "admin_assistant", "hr"].includes(user?.role || "");
  const p = typeof window !== "undefined" && window.innerWidth < 480 ? 16 : 24;

  const [answered, setAnswered] = useState<boolean | null>(null);
  const [question, setQuestion] = useState(DEFAULT_QUESTION);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<any | null>(null);

  // HR question editor
  const [editingQ, setEditingQ] = useState(false);
  const [draftQ, setDraftQ] = useState("");
  const [savingQ, setSavingQ] = useState(false);

  useEffect(() => {
    getPulseStatus().then(s => {
      setAnswered(s.answered);
      if (s.question) setQuestion(s.question);
    }).catch(() => setAnswered(false));
    if (isHR) getPulseResults().then(r => {
      setResults(r);
      if (r.question) setQuestion(r.question);
    }).catch(() => {});
  }, [isHR]);

  async function handleSubmit() {
    if (score === null) { toast("Pick a score first", "error"); return; }
    setSubmitting(true);
    try {
      await submitPulse({ score, comment: comment.trim() || undefined });
      setAnswered(true);
      toast("Thanks for your feedback!", "success");
      if (isHR) getPulseResults().then(setResults).catch(() => {});
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to submit", "error");
    } finally { setSubmitting(false); }
  }

  async function handleSaveQuestion() {
    if (!draftQ.trim()) { toast("Question cannot be empty", "error"); return; }
    setSavingQ(true);
    try {
      const r = await setPulseQuestion(draftQ.trim());
      setQuestion(r.question);
      setEditingQ(false);
      toast("Question updated for this month", "success");
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to save", "error");
    } finally { setSavingQ(false); }
  }

  const monthName = new Date().toLocaleString("default", { month: "long", year: "numeric" });

  return (
    <div style={{ padding: p, maxWidth: 860, margin: "0 auto" }}>
      <LoadingOverlay active={submitting || savingQ} />
      {/* The house `.page-header` block. A hand-rolled navy <h2> here read as a
          different kind of heading from the black <h1> every sibling page uses. */}
      <div className="page-header">
        <div>
          <h1>Pulse Survey</h1>
          <p>
            One quick anonymous question a month — your honest answer helps make this a better place to work
          </p>
        </div>
      </div>

      {/* ── HR: question editor ── */}
      {isHR && (
        <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                This month's question
              </div>
              {editingQ ? (
                <textarea
                  value={draftQ}
                  onChange={e => setDraftQ(e.target.value)}
                  rows={2}
                  maxLength={500}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `1.5px solid ${NAVY}`, fontSize: 14, resize: "vertical", boxSizing: "border-box" }}
                />
              ) : (
                <div style={{ fontSize: 14, color: "#0f172a", fontWeight: 600 }}>{question}</div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
              {editingQ ? (
                <>
                  <button onClick={() => setEditingQ(false)}
                    style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "7px 14px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    Cancel
                  </button>
                  <button disabled={savingQ} onClick={handleSaveQuestion}
                    style={{ background: NAVY, color: "white", border: "none", padding: "7px 16px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    {savingQ ? "Saving…" : "Save"}
                  </button>
                </>
              ) : (
                <button onClick={() => { setDraftQ(question); setEditingQ(true); }}
                  style={{ background: "white", color: NAVY, border: `1.5px solid ${NAVY}`, padding: "7px 14px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                  Edit question
                </button>
              )}
            </div>
          </div>
          {editingQ && (
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
              Changing the question only affects this month. Previous months keep their original question.
            </div>
          )}
        </div>
      )}

      {/* ── Employee submit card ── */}
      {answered === false ? (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 16, padding: 28, marginBottom: 32 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a", marginBottom: 6 }}>
            {question}
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 20 }}>
            {monthName} · completely anonymous — nobody can see who answered what
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {Array.from({ length: 11 }, (_, n) => (
              <button key={n} onClick={() => setScore(n)} style={{
                width: 44, height: 44, borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 15,
                border: score === n ? `2.5px solid ${NAVY}` : "1.5px solid #e2e8f0",
                background: score === n ? NAVY : "white",
                color: score === n ? "white" : scoreColor(n),
              }}>{n}</button>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#94a3b8", maxWidth: 540, marginBottom: 18 }}>
            <span>Not at all likely</span><span>Extremely likely</span>
          </div>
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3}
            placeholder="Anything you'd like to share? (optional, anonymous)"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 16, resize: "vertical", boxSizing: "border-box" }} />
          <button disabled={submitting || score === null} onClick={handleSubmit}
            style={{ background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white", border: "none", padding: "11px 28px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 14, opacity: score === null ? 0.5 : 1 }}>
            {submitting ? "Submitting…" : "Submit Anonymously"}
          </button>
        </div>
      ) : answered === true ? (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "18px 24px", marginBottom: 32, color: "#166534", fontSize: 14, fontWeight: 600 }}>
          ✓ You've responded for {monthName}. Thank you! Next pulse opens on the 1st.
        </div>
      ) : null}

      {/* ── HR results ── */}
      {isHR && results && (
        <>
          <h3 style={{ fontSize: 16, color: "#334155", marginBottom: 14 }}>Results (anonymous)</h3>

          <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
            {(() => {
              const current = results.trend.find((t: any) => t.period === results.current_period);
              const enps = current ? current.enps : null;
              return (
                <>
                  <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 24px", flex: 1, minWidth: 150 }}>
                    <div style={{ fontSize: 30, fontWeight: 800, color: enps == null ? "#94a3b8" : enps >= 0 ? "#16a34a" : "#dc2626" }}>
                      {enps == null ? "—" : enps > 0 ? `+${enps}` : enps}
                    </div>
                    <div style={{ fontSize: 13, color: "#64748b" }}>eNPS this month</div>
                  </div>
                  <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 24px", flex: 1, minWidth: 150 }}>
                    <div style={{ fontSize: 30, fontWeight: 800, color: NAVY }}>
                      {results.participation.responses}/{results.participation.headcount}
                    </div>
                    <div style={{ fontSize: 13, color: "#64748b" }}>Participation</div>
                  </div>
                  <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 24px", flex: 1, minWidth: 150 }}>
                    <div style={{ fontSize: 30, fontWeight: 800, color: "#0f172a" }}>
                      {current ? current.avg_score : "—"}
                    </div>
                    <div style={{ fontSize: 13, color: "#64748b" }}>Average score</div>
                  </div>
                </>
              );
            })()}
          </div>

          {results.trend.length > 0 && (
            <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20, marginBottom: 24, overflowX: "auto" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.05em" }}>Trend</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ color: "#94a3b8", textAlign: "left" }}>
                    <th style={{ padding: "6px 10px" }}>Month</th>
                    <th style={{ padding: "6px 10px" }}>eNPS</th>
                    <th style={{ padding: "6px 10px" }}>Avg</th>
                    <th style={{ padding: "6px 10px" }}>Promoters</th>
                    <th style={{ padding: "6px 10px" }}>Passives</th>
                    <th style={{ padding: "6px 10px" }}>Detractors</th>
                    <th style={{ padding: "6px 10px" }}>Responses</th>
                  </tr>
                </thead>
                <tbody>
                  {[...results.trend].reverse().map((t: any) => (
                    <tr key={t.period} style={{ borderTop: "1px solid #f1f5f9", color: "#334155" }}>
                      <td style={{ padding: "8px 10px", fontWeight: 600 }}>{t.period}</td>
                      <td style={{ padding: "8px 10px", fontWeight: 700, color: t.enps >= 0 ? "#16a34a" : "#dc2626" }}>
                        {t.enps > 0 ? `+${t.enps}` : t.enps}
                      </td>
                      <td style={{ padding: "8px 10px" }}>{t.avg_score}</td>
                      <td style={{ padding: "8px 10px" }}>{t.promoters}</td>
                      <td style={{ padding: "8px 10px" }}>{t.passives}</td>
                      <td style={{ padding: "8px 10px" }}>{t.detractors}</td>
                      <td style={{ padding: "8px 10px" }}>{t.responses}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Recent comments (anonymous)
            </div>
            {results.comments.length === 0 ? (
              <div style={{ fontSize: 13, color: "#94a3b8" }}>No comments yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {results.comments.map((c: any, i: number) => (
                  <div key={i} style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px" }}>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
                      {c.period} · scored <strong style={{ color: scoreColor(c.score) }}>{c.score}/10</strong>
                    </div>
                    <div style={{ fontSize: 13, color: "#334155" }}>{c.comment}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
